import type { ICliente } from "./entidades/ICliente";
import type { IFiltroPaginador, IRespuestaApiLista, IRespuestaApiObjeto } from "../../globales/interfaces";
import type { IClienteContratoHistorial } from "../ClientesContratos/entidades/IClienteContratoHistorial";
import api from "../../utils/api";
import type { IClienteHistorial } from "../ClientesContratos/entidades/IClienteHistorial";
import type { IFiltroCliente } from "./entidades/IFiltroCliente";

export class ClienteModelo {
    

    constructor() { }

    public async obtenerListaClientes(filtro: IFiltroCliente): Promise<IRespuestaApiLista<ICliente>> {
        const { data } = await api.post(
            `/api/Clientes/ListarCompuesto`, filtro, {
            headers: {
                "Content-Type": "application/json",
            }
        });

        return data;
    }

    public async obtenerClientePorId(idCliente: number): Promise<IRespuestaApiObjeto<ICliente>> {
        const { data } = await api.get(
            `/api/Clientes/ObtenerPorId/${idCliente}`, {
            headers: {
                "Content-Type": "application/json",
                // "Authorization": "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIyIiwiZW1haWwiOiJ1c3VhcmlvcHJ1ZWJhc0BnbWFpbC5jb20iLCJodHRwOi8vc2NoZW1hcy54bWxzb2FwLm9yZy93cy8yMDA1LzA1L2lkZW50aXR5L2NsYWltcy9uYW1lIjoiSnVhbiBQZXJleiIsImp0aSI6IjNhZWVmYzNmLWFhZjMtNDAzMS04YmM4LWE4NDFiMDRmMzBhZSIsIm5iZiI6MTc1OTIwMjc0MiwiZXhwIjoxNzU5MjMxNTQyLCJpc3MiOiJjb25lY2t0LWxvY2FsIiwiYXVkIjoiY29uZWNrdF9jbGllbnRzIn0.EUDwR_TLUto2cA-_bcWeWmjSzt8fByCiOaEFyRKe8aE"
            },
        });
        return data;
    }

    public async obtenerListaClientesCompuesto(filtro: Partial<ICliente>, filtroPaginador: IFiltroPaginador): Promise<IRespuestaApiLista<ICliente>> {
        const { data } = await api.post(
            `/api/Clientes/ListarCompuesto`, {
            ...filtro,
            ...filtroPaginador
        }, {
            headers: {
                "Content-Type": "application/json",
            },
            // params: {
            //     id: filtro.Id,
            //     identificacion: filtro.Identificacion,
            //     razonSocial: filtro.RazonSocial,
            //     page: filtroPaginador.Page,
            //     size: filtroPaginador.Size
            // },
        });

        return data;
    }

    public async obtenerListaHistorialClientesCompuesto(proyectoId: number, filtroPaginador: IFiltroPaginador): Promise<IRespuestaApiLista<IClienteHistorial>> {
        const { data } = await api.get(
            `/api/HistorialClienteContratoProyectos/ListarCompuesto`, {
            headers: {
                "Content-Type": "application/json"
            },
            params: {
                proyectoid: proyectoId,
                page: filtroPaginador.Page,
                size: filtroPaginador.Size
            },
        });

        // Aseguramos que data.Data sea un arreglo de IClienteContratoHistorial
        const lista: IClienteContratoHistorial[] = Array.isArray(data.Data) ? data.Data : [];

        // 1. Ordenar la lista cronológicamente (más antiguo a más reciente)
        // Esto es crucial para determinar correctamente las fechas de inicio.
        lista.sort((a, b) => new Date(a.FechaUtc).getTime() - new Date(b.FechaUtc).getTime());

        // 2. Mapa para rastrear la fecha de inicio de cada cliente.
        // Key: ID del cliente (IdClienteNuevo) | Value: FechaUtc
        const fechaInicioMap = new Map<number, string>();

        // 3. Mapa para almacenar los clientes anteriores enriquecidos con fechas.
        // Usamos un Map para garantizar que cada cliente anterior sea único.
        const clientesAnterioresMap = new Map<number, IClienteHistorial>();

        // 4. Iteración para calcular Fechas de Inicio y Fechas de Fin
        for (const item of lista) {
            const clienteNuevoId = item.IdClienteNuevo;
            const clienteAnteriorId = item.IdClienteAnterior;
            const fechaCambio = item.FechaUtc;

            // A. Rastrear Fecha de Inicio
            // La Fecha de Inicio es la primera FechaUtc donde el cliente aparece como Nuevo.
            if (clienteNuevoId && !fechaInicioMap.has(clienteNuevoId)) {
                fechaInicioMap.set(clienteNuevoId, fechaCambio);
            }

            // B. Calcular Fecha de Fin para el Cliente Anterior
            if (clienteAnteriorId && item.ClienteAnterior) {

                // La Fecha de Fin del Cliente Anterior es la FechaUtc de este registro, 
                // ya que aquí fue reemplazado.
                const fechaFin = fechaCambio;

                // Si el cliente anterior ya fue agregado al mapa, significa que fue reemplazado
                // en un historial posterior, por lo que su información ya está consolidada.
                // Si lo agregamos por primera vez:
                if (!clientesAnterioresMap.has(clienteAnteriorId)) {

                    // Intentamos obtener la Fecha de Inicio que rastreamos previamente
                    const fechaInicio = fechaInicioMap.get(clienteAnteriorId);

                    if (fechaInicio) {
                        const clienteConFechas: IClienteHistorial = {
                            ...item.ClienteAnterior, // Datos base del cliente anterior
                            FechaInicioAsignacionCliente: fechaInicio,
                            FechaFinAsignacionCliente: fechaFin,
                        };
                        clientesAnterioresMap.set(clienteAnteriorId, clienteConFechas);
                    }
                }
            }
        }

        // 5. Convertir el Mapa de clientes anteriores a un Array y asignarlo a data.Data
        data.Data = Array.from(clientesAnterioresMap.values());

        return data as Promise<IRespuestaApiLista<IClienteHistorial>>;
    }

    public async crearCliente(cliente: ICliente): Promise<IRespuestaApiObjeto<ICliente>> {
        const { data } = await api.post(
            `/api/Clientes/Crear`, cliente, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        return data;
    }

    public async editarCliente(cliente: ICliente): Promise<IRespuestaApiObjeto<ICliente>> {
        const { data } = await api.put(
            `/api/Clientes/Actualizar`, cliente, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        return data;
    }

    public async eliminarCliente(cliente: ICliente): Promise<IRespuestaApiObjeto<boolean>> {
        const { data } = await api.delete(
            `/api/Clientes/Actualizar/${cliente.Id}`, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        return data;
    }
}

